import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import dayjs from "dayjs";
import React from "react";

import {
  ClientRoundAnalytics,
  WeekJobPerformance,
  WeeklyPerformanceData,
} from "@/types/job";

import { formatDate } from "./utils/helpers";
import { TableOverviewCard } from "./utils/OverviewCards";

type Props = {
  data: WeeklyPerformanceData;
};

const WeekJobPerformanceTable: React.FC<Props> = ({ data }) => {
  const allClientRounds = React.useMemo(() => {
    const map = new Map<number, ClientRoundAnalytics>();

    data?.weekly_performance?.forEach((week) => {
      week.client_round_analytics?.forEach((round) => {
        if (!map.has(round.round_id)) {
          map.set(round.round_id, round);
        }
      });
    });

    return Array.from(map.values());
  }, [data]);

  const [sorting, setSorting] = React.useState<SortingState>([
    { id: "week", desc: true },
  ]);

  const columns = React.useMemo<ColumnDef<WeekJobPerformance>[]>(() => {
    const staticColumns: ColumnDef<WeekJobPerformance>[] = [
      {
        header: "Week",
        id: "week",
        accessorFn: (row) => dayjs(row.from).valueOf(),
        cell: ({ row }) => row.original.week,
        enableSorting: true,
      },
      {
        header: "Date Range",
        id: "date_range",
        cell: ({ row }) =>
          `${formatDate(row.original.from)}-${formatDate(row.original.to)}`,
      },
      { header: "Sourced", accessorKey: "sourced" },
      { header: "Manual Screening", accessorKey: "manual_screening" },
      { header: "Manual Qualified", accessorKey: "manual_qualified" },
      { header: "AI Screening", accessorKey: "ai_screening" },
      { header: "AI Qualified", accessorKey: "ai_qualified" },
      { header: "To Schedule", accessorKey: "to_schedule" },
      { header: "Scheduled", accessorKey: "scheduled" },
      {
        header: "Z-Interview Completed",
        accessorKey: "z_interview_completed",
      },
      {
        header: "Z-Interview Qualified",
        accessorKey: "z_interview_qualified",
      },
      {
        header: "Client Submissions",
        accessorKey: "client_submissions",
      },
    ];

    const dynamicRoundColumns: ColumnDef<WeekJobPerformance>[] =
      allClientRounds.map((round) => ({
        id: `round_${round.round_id}`,
        header: `${round.category} – ${round.round_name}`,
        cell: ({ row }) => {
          const match = row.original.client_round_analytics?.find(
            (r) => r.round_id === round.round_id,
          );
          return match?.completed_count ?? 0;
        },
      }));

    const tailColumns: ColumnDef<WeekJobPerformance>[] = [
      { header: "Offers", accessorKey: "offers_released" },
      { header: "Joined", accessorKey: "candidates_joined" },
    ];

    return [...staticColumns, ...dynamicRoundColumns, ...tailColumns];
  }, [allClientRounds]);

  const table = useReactTable({
    data: data?.weekly_performance || [],
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div>
      <div>{data?.overall && <TableOverviewCard overall={data.overall} />}</div>
      <div className="my-2 max-h-[420px] w-full overflow-x-auto overflow-y-auto rounded border">
        <table className="min-w-[1200px] border-collapse">
          <thead className="bg-slate-100">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={`sticky top-0 border bg-slate-100 px-3 py-2 text-left text-xs font-semibold text-gray-700 ${
                      header.column.id === "week"
                        ? "left-0 z-30 min-w-[120px] shadow-[2px_0_4px_rgba(0,0,0,0.08)]"
                        : header.column.id === "date_range"
                          ? "left-[120px] z-30 min-w-[160px] shadow-[2px_0_4px_rgba(0,0,0,0.08)]"
                          : "z-20 min-w-[120px]"
                    } `}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        className={`flex items-center gap-1 ${
                          header.column.getCanSort()
                            ? "cursor-pointer select-none"
                            : ""
                        }`}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}

                        {header.column.getIsSorted() === "asc" && "▲"}
                        {header.column.getIsSorted() === "desc" && "▼"}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="hover:bg-slate-50">
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={`border bg-white px-3 py-2 text-sm text-gray-800 ${
                      cell.column.id === "week"
                        ? "sticky left-0 z-20 min-w-[120px] shadow-[2px_0_4px_rgba(0,0,0,0.08)]"
                        : cell.column.id === "date_range"
                          ? "sticky left-[120px] z-20 min-w-[160px] shadow-[2px_0_4px_rgba(0,0,0,0.08)]"
                          : ""
                    } `}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WeekJobPerformanceTable;
